import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import Avatar from 'flarum/common/components/Avatar';
import Link from 'flarum/common/components/Link';
import username from 'flarum/common/helpers/username';
import humanTime from 'flarum/common/helpers/humanTime';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';

/**
 * Replaces flarum/flags' FlagsPage on /flags with a moderation work list:
 * every row shows who flagged the post and why, and posts can be approved,
 * dismissed or deleted per row or in bulk — all through the site's
 * `POST /api/posts/bulk-moderate` endpoint, so single-row and bulk actions
 * behave identically (approving also dismisses the flags; deleting a first
 * post removes its discussion).
 */
export default class ModerationFlagsPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    app.history.push('flags');
    this.bodyClass = 'App--flags';

    // Selected POST ids: the endpoint works on posts, and the flags list
    // carries one row per post.
    this.selected = new Set();
    this.working = false;
    app.flags.load();
  }
  flags() {
    return app.flags.getPages().flatMap(page => page.items);
  }
  view() {
    const flags = this.flags();
    const loading = app.flags.isLoading();
    return m("div", {
      className: "ModerationFlagsPage"
    }, m("div", {
      className: "container"
    }, m("h2", {
      className: "ModerationFlagsPage-title"
    }, app.translator.trans('bhic.forum.moderation.title')), flags.length ? this.bulkBar(flags) : null, loading && !flags.length ? m(LoadingIndicator, null) : null, !loading && !flags.length ? m("p", {
      className: "helpText"
    }, app.translator.trans('bhic.forum.moderation.empty_text')) : null, m("ul", {
      className: "ModerationFlagsPage-list"
    }, flags.map(flag => this.row(flag))), app.flags.hasNext() ? m("div", {
      className: "ModerationFlagsPage-loadMore"
    }, m(Button, {
      className: "Button",
      loading: app.flags.isLoadingNext(),
      onclick: () => app.flags.loadNext()
    }, app.translator.trans('bhic.forum.moderation.load_more_button'))) : null));
  }
  bulkBar(flags) {
    const allIds = flags.map(flag => flag.post().id());
    const allSelected = allIds.every(id => this.selected.has(id));
    const count = this.selected.size;
    return m("div", {
      className: "ModerationFlagsPage-bulkBar"
    }, m("label", {
      className: "checkbox ModerationFlagsPage-selectAll"
    }, m("input", {
      type: "checkbox",
      checked: allSelected,
      onchange: () => {
        if (allSelected) {
          this.selected.clear();
        } else {
          allIds.forEach(id => this.selected.add(id));
        }
      }
    }), app.translator.trans('bhic.forum.moderation.select_all_label')), m("span", {
      className: "ModerationFlagsPage-selectedCount"
    }, app.translator.trans('bhic.forum.moderation.selected_count', {
      count
    })), m("div", {
      className: "ButtonGroup"
    }, this.actionButtons(() => [...this.selected], !count)));
  }
  row(flag) {
    const post = flag.post();
    const postId = post.id();
    return m("li", {
      className: 'ModerationFlagsPage-item' + (this.selected.has(postId) ? ' selected' : ''),
      key: flag.id()
    }, m("input", {
      type: "checkbox",
      className: "ModerationFlagsPage-itemCheckbox",
      checked: this.selected.has(postId),
      onchange: () => {
        if (this.selected.has(postId)) {
          this.selected.delete(postId);
        } else {
          this.selected.add(postId);
        }
      }
    }), m(Avatar, {
      user: post.user() || null
    }), m("div", {
      className: "ModerationFlagsPage-itemMain"
    }, m("div", {
      className: "ModerationFlagsPage-itemHeader"
    }, m(Link, {
      href: app.route.post(post)
    }, app.translator.trans('flarum-flags.forum.flagged_posts.item_text', {
      username: username(post.user()),
      em: m("em", null),
      discussion: post.discussion().title()
    })), m("span", {
      className: "ModerationFlagsPage-itemTime"
    }, humanTime(flag.createdAt()))), m("div", {
      className: "ModerationFlagsPage-itemExcerpt"
    }, post.contentPlain()), m("div", {
      className: "ModerationFlagsPage-itemFlag"
    }, this.flagInfo(flag))), m("div", {
      className: "ButtonGroup ModerationFlagsPage-itemActions"
    }, this.actionButtons(() => [postId], false)));
  }

  /**
   * The same three actions serve the bulk bar and each row; `ids` is lazy so
   * the bulk buttons always act on the selection at click time.
   */
  actionButtons(ids, disabled) {
    return [m(Button, {
      className: "Button",
      icon: "fas fa-check",
      disabled: disabled || this.working,
      onclick: () => this.bulk('approve', ids())
    }, app.translator.trans('bhic.forum.moderation.approve_button')), m(Button, {
      className: "Button",
      icon: "far fa-eye-slash",
      disabled: disabled || this.working,
      onclick: () => this.bulk('dismiss-flags', ids())
    }, app.translator.trans('bhic.forum.moderation.dismiss_button')), m(Button, {
      className: "Button",
      icon: "fas fa-times",
      disabled: disabled || this.working,
      onclick: () => this.bulk('delete', ids())
    }, app.translator.trans('bhic.forum.moderation.delete_button'))];
  }
  flagInfo(flag) {
    const detail = flag.reasonDetail();
    let label;
    if (flag.type() === 'cleantalk') {
      label = app.translator.trans('linku-cleantalk.forum.post.cleantalk_flagged_text');
    } else if (flag.type() === 'user') {
      const reason = flag.reason();
      label = [app.translator.trans('bhic.forum.moderation.flagged_by', {
        username: username(flag.user())
      }), reason ? [': ', app.translator.trans("flarum-flags.forum.flag_post.reason_".concat(reason, "_label"))] : null];
    } else {
      label = flag.type();
    }
    return [m("span", {
      className: "ModerationFlagsPage-flagLabel"
    }, label), detail ? m("span", {
      className: "ModerationFlagsPage-flagDetail"
    }, detail) : null];
  }
  bulk(action, ids) {
    if (!ids.length || this.working) return;
    if (action === 'delete' && !confirm(extractText(app.translator.trans('core.forum.post_controls.delete_confirmation')))) return;
    this.working = true;
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/posts/bulk-moderate',
      body: {
        action,
        ids
      }
    }).then(() => {
      ids.forEach(id => this.selected.delete(id));
      return app.flags.refresh();
    }).finally(() => {
      this.working = false;
      m.redraw();
    });
  }
}
flarum.reg.add('flarum-flarum', 'forum/components/ModerationFlagsPage', ModerationFlagsPage);